/**
 * PrivatFlyout - "Rettigheter og krav" flyout-meny
 * Veiledning for arbeidstakere om lønnskrav, feriepenger og arbeidstid
 * @version 2.1.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scale, X, ChevronRight, Wallet, Sun, Clock,
  CheckCircle, ArrowLeft, FileText, Calculator, AlertCircle,
  Shield, Sparkles
} from 'lucide-react';

// ============================================================
// RETTIGHETER OG KRAV MODULER
// ============================================================
const RETTIGHETER_MODULES = [
  {
    id: 'lonnskrav',
    name: 'Lønnskrav',
    description: 'AI-analyse av lønnskrav og generering av kravbrev',
    icon: Wallet,
    color: '#22c55e',
    status: 'active',
    route: '/rettigheter/lonnskrav',
    features: ['AI-analyse av arbeidsgivers svar', 'Automatisk kravbrev-generering', 'PDF-eksport med branding']
  },
  {
    id: 'feriepenger',
    name: 'Feriepenger',
    description: 'Sjekk om feriepengene dine er riktig beregnet',
    icon: Sun,
    color: '#f59e0b',
    status: 'active',
    route: '/rettigheter/feriepenger',
    features: ['Beregningsanalyse', 'Identifiser mangler i grunnlaget', 'Generer kravbrev']
  },
  {
    id: 'arbeidstid',
    name: 'Arbeidstid og overtid',
    description: 'Vurder lovlighet og beregn overtidskrav',
    icon: Clock,
    color: '#3b82f6',
    status: 'active',
    route: '/rettigheter/arbeidstid',
    features: ['Sjekk lovlighet', 'Beregn overtidstillegg', 'Hviletidsvurdering']
  }
];

// ============================================================
// FLYOUT COMPONENT
// ============================================================
export function PrivatFlyout({ isOpen, onClose }) {
  const panelRef = useRef(null);
  const navigate = useNavigate();

  // Lukk ved klikk utenfor
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        // Sjekk om klikket var på "Rettigheter og krav"-knappen
        const triggerButton = document.querySelector('[data-testid="privat-flyout-trigger"]');
        if (triggerButton && triggerButton.contains(event.target)) {
          return;
        }
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Lukk med Escape-tast
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleModuleClick = (module) => {
    onClose();
    navigate(module.route);
  };

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 md:hidden ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Flyout Panel */}
      <div
        ref={panelRef}
        data-testid="privat-flyout-panel"
        className={`
          fixed z-50
          bg-[#131C2E]/98 backdrop-blur-xl
          border border-white/10 shadow-2xl
          will-change-transform
          
          /* Desktop: til høyre for sidebar (288px = sidebar width) */
          md:left-[288px] md:top-0 md:bottom-auto md:w-80 md:rounded-r-lg md:rounded-l-none
          
          /* Mobile: bunn-meny */
          left-0 right-0 bottom-0 rounded-t-2xl md:right-auto md:bottom-auto
          
          /* GPU-akselerert animasjon */
          transition-[transform,opacity] duration-300 ease-out
          ${isOpen 
            ? 'translate-y-0 md:translate-x-0 opacity-100' 
            : 'translate-y-full md:translate-y-0 md:-translate-x-4 opacity-0 pointer-events-none'
          }
        `}
        style={{
          maxHeight: 'calc(100vh - 100px)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-[#C07850]" />
            <h2 className="text-sm font-bold text-[#F0F4F8]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Rettigheter og krav
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/5 text-[#8899A8] hover:text-[#F0F4F8] transition-colors"
            data-testid="close-privat-flyout"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Info banner */}
        <div className="px-4 py-3 border-b border-white/5 bg-[#1B2740]/30">
          <p className="text-xs text-[#8899A8] leading-relaxed">
            Veiledning og verktøy for arbeidstakere om lønn, feriepenger og arbeidstid.
          </p>
        </div>

        {/* Module List */}
        <div className="p-2 max-h-[60vh] md:max-h-none overflow-y-auto">
          {RETTIGHETER_MODULES.map((module) => {
            const Icon = module.icon;

            return (
              <button
                key={module.id}
                onClick={() => handleModuleClick(module)}
                data-testid={`rettighet-module-${module.id}`}
                className="
                  w-full flex items-center gap-3 p-3 rounded-lg mb-1 text-left
                  transition-all duration-200 group
                  hover:bg-white/5 border border-transparent
                "
              >
                {/* Icon */}
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                  style={{ backgroundColor: `${module.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: module.color }} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-[#B8C8D8] group-hover:text-[#F0F4F8] block">
                    {module.name}
                  </span>
                  <p className="text-xs text-[#8899A8] mt-0.5 line-clamp-2">
                    {module.description}
                  </p>
                  {/* Features preview */}
                  <div className="flex items-center gap-1 mt-1">
                    <Sparkles className="w-3 h-3 text-[var(--copper)]" />
                    <span className="text-[10px] text-[var(--copper)]">AI-analyse</span>
                  </div>
                </div>

                {/* Arrow */}
                <ChevronRight className="w-4 h-4 flex-shrink-0 text-[#8899A8] group-hover:translate-x-0.5 transition-transform" />
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-white/5">
          <p className="text-[10px] text-[#8899A8] text-center">
            Alle verktøy er basert på norsk arbeidsrett
          </p>
        </div>
      </div>
    </>
  );
}

// ============================================================
// TRIGGER BUTTON
// ============================================================
export function PrivatTriggerButton({ onClick, isOpen }) {
  return (
    <button
      onClick={onClick}
      data-testid="privat-flyout-trigger"
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg w-full
        text-sm font-medium transition-all duration-200
        ${isOpen 
          ? 'bg-[#C07850]/20 text-[#C07850] border border-[#C07850]/30' 
          : 'text-[#8899A8] hover:text-[#F0F4F8] hover:bg-[#1B2740]/50 border border-transparent'
        }
      `}
    >
      <Scale className="w-4 h-4" />
      <span className="btn-text">Rettigheter og krav</span>
      <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${isOpen ? 'rotate-90' : ''}`} />
    </button>
  );
}

export default PrivatFlyout;
